import { ImportRecord, ImportStatus } from '@/types/fileImport';
import { create } from 'zustand';
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { MAX_AUDIO_COUNT } from '@/constants/fileImport';
interface FileImportState {
  fileRecords: Array<ImportRecord>; // 任务列表
  benefit: number; // 最大权益数
  benefitUsed: number; // 已经使用的权益数
  uploadingCount: number;// 正在上传的文件个数
  ffmpeg: FFmpeg | null,
}

interface FileImportStore extends FileImportState {
  setBenfit: (total: number, used: number) => void;
  addUploadRecords: (record: ImportRecord) => void; // 新增文件上传记录
  removeUploadRecord: (id: string) => void; // 移除文件上传记录
  updateSingleRecord: (id: string, newRecord: {
    newId?: string;
    status?: number;
    remainTime?: number;
    uploadProcess?: number;
    fileKey?: string;
    fileSize?: number
  }) => void;
  setFFmpeg: (ffmpeg: FFmpeg) => void;
  resetStore: () => void;
}

const initFileImportState: FileImportState = {
  fileRecords: [],
  benefit: MAX_AUDIO_COUNT,
  benefitUsed: 0,
  uploadingCount: 0,
  ffmpeg: null,
}

const fileImportStore = create<FileImportStore>()((set, get) => ({
  ...initFileImportState,
  setBenfit: (total, used) => {
    set({ benefit: total, benefitUsed: used });
  },
  addUploadRecords: (record: ImportRecord) => {
    const currRecords = get().fileRecords;
    currRecords.unshift(record);
    
    // 新增上传任务时，如果状态是上传状态，则 uploadingCount +1
    const isUploadStatus = record.status === ImportStatus.WAIT_UPLOAD || record.status === ImportStatus.UPLOADING;
    if (isUploadStatus) {
      const state = get();
      set({ 
        fileRecords: currRecords,
        uploadingCount: state.uploadingCount + 1
      });
    } else {
      set({ fileRecords: currRecords });
    }
  },
  removeUploadRecord: (id) => {
    const currRecords = get().fileRecords;
    const newRecords: ImportRecord[] = [];
    let uploadingCountDelta = 0;

    console.log('removeUploadRecord', id);

    for (let i = 0; i < currRecords.length; i++) {
      const item = currRecords[i];
      if (id !== item.id) {
        newRecords.push(item);
      } else {
        // 如果要删除的记录状态是上传状态，需要减少 uploadingCount
        const isUploadStatus = item.status === ImportStatus.WAIT_UPLOAD ||
          item.status === ImportStatus.UPLOADING;
        if (isUploadStatus) {
          uploadingCountDelta = -1;
        }
      }
    }
    const state = get();
    const newUploadingCount = Math.max(0, state.uploadingCount + uploadingCountDelta);
    set({ fileRecords: newRecords, uploadingCount: newUploadingCount });
  },
  updateSingleRecord: (id: string, newRecord: {
    newId?: string;
    status?: number;
    remainTime?: number;
    uploadProcess?: number;
    fileKey?: string;
    fileSize?: number
  },) => {
    const currRecords = get().fileRecords;

    if (currRecords.length === 0) {
      set({ uploadingCount: 1 });
      return;
    }

    const newRecords: ImportRecord[] = [];
    let uploadingCountDelta = 0;
    let benefitUsedDelta = 0;

    for (let i = 0; i < currRecords.length; i++) {
      const item = currRecords[i];
      if (id === item.id) {
        const { newId, status, remainTime, uploadProcess, fileKey, fileSize } = newRecord;
        const oldStatus = item.status;
        const newStatus = status ?? item.status;

        // 判断是否为上传状态（WAIT_UPLOAD 或 UPLOADING）
        const isOldUploadStatus = oldStatus === ImportStatus.WAIT_UPLOAD || oldStatus === ImportStatus.UPLOADING;
        const isNewUploadStatus = newStatus === ImportStatus.WAIT_UPLOAD || newStatus === ImportStatus.UPLOADING;

        // 判断是否为转写状态（WAITING, TRANSING, TRANS_SUCCESS）
        const isOldTransformStatus = oldStatus === ImportStatus.WAITING || 
                                     oldStatus === ImportStatus.TRANSING || 
                                     oldStatus === ImportStatus.TRANS_SUCCESS;
        const isNewTransformStatus = newStatus === ImportStatus.WAITING || 
                                     newStatus === ImportStatus.TRANSING || 
                                     newStatus === ImportStatus.TRANS_SUCCESS;

        // 判断是否为错误/取消状态（TRANS_ERROR, CANCEL, ADD_ERROR）
        const isNewErrorStatus = newStatus === ImportStatus.TRANS_ERROR || 
                                 newStatus === ImportStatus.CANCEL || 
                                 newStatus === ImportStatus.ADD_ERROR;

        // uploadingCount 逻辑：新增上传任务 +1，上传完成/错误 -1
        // 从非上传状态变为上传状态
        if (!isOldUploadStatus && isNewUploadStatus) {
          uploadingCountDelta += 1;
        }
        // 从上传状态变为非上传状态
        if (isOldUploadStatus && !isNewUploadStatus) {
          uploadingCountDelta -= 1;
        }

        // benefitUsed 逻辑：进入转写环节 +1，转写错误/取消转写/新增错误 -1
        // 从上传状态变为转写状态
        if (isOldUploadStatus && isNewTransformStatus) {
          benefitUsedDelta += 1;
        }
        // 从转写状态变为错误/取消状态
        if (isOldTransformStatus && isNewErrorStatus) {
          benefitUsedDelta -= 1;
        }

        newRecords.push({
          id: newId ?? item.id,
          fileName: item.fileName,
          fileType: item.fileType,
          status: newStatus,
          remainTime: remainTime ?? item.remainTime,
          uploadProcess: uploadProcess ?? item.uploadProcess,
          fileKey: fileKey ?? item.fileKey,
          fileSize: fileSize ?? item.fileSize
        });
      } else {
        newRecords.push(item);
      }
    }

    const state = get();
    const newUploadingCount = Math.max(0, state.uploadingCount + uploadingCountDelta);
    const newBenefitUsed = Math.max(0, state.benefitUsed + benefitUsedDelta);
    set({ 
      fileRecords: newRecords, 
      uploadingCount: newUploadingCount,
      benefitUsed: newBenefitUsed
    });
  },
  resetStore: () => {
    set({
      fileRecords: [],
      benefit: MAX_AUDIO_COUNT,
      benefitUsed: 0,
      uploadingCount: 0,
      ffmpeg: null
    });
  },
  setFFmpeg: (ffmpeg: FFmpeg) => {
    set({ ffmpeg });
  },
}));

export default fileImportStore;